import { useEffect, useMemo, useState } from "react";
import { FlaskConical, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const fmtSigned = (v) => (v == null ? "—" : `${v < 0 ? "−" : v > 0 ? "+" : ""}${fmtSEK(Math.abs(v))}`);
const MONTH_LABEL = (ym) => {
  if (!ym) return "—";
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
};

const EMPTY_FORM = {
  name: "",
  description: "",
  employment_start: "",
  assignment_start: "",
  fte_monthly_salary: "",
  pensionOverride: false,
  pension_amount: "",
  utilization_pct: "80",
  hourly_rate: "",
  payment_terms_id: "",
  vat_rate_id: "",
};

export default function ScenariosPage() {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [companies, setCompanies] = useState([]);
  const [years, setYears] = useState([]);
  const [scenarios, setScenarios] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [vatRates, setVatRates] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [estimate, setEstimate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/payment-terms"), api.get("/vat/rates")])
      .then(([c, pt, vr]) => {
        setCompanies(c.data);
        setPaymentTerms(pt.data.filter((t) => t.active !== false));
        setVatRates(vr.data.filter((v) => v.active !== false));
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  const load = async () => {
    if (!companyId || !year) return;
    try {
      const [s, cmp] = await Promise.all([
        api.get("/scenarios", { params: { company_id: companyId, year } }),
        api.get("/scenario-outcome/comparison", { params: { company_id: companyId, year } }),
      ]);
      setScenarios(s.data);
      setComparison(cmp.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load().catch(() => {});
  }, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = async () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setPreview(null);
    setEstimate(null);
    setDialogOpen(true);
    try {
      const res = await api.get("/scenarios/pension-estimate", { params: { company_id: companyId } });
      setEstimate(res.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description || "",
      employment_start: s.employment_start,
      assignment_start: s.assignment_start,
      fte_monthly_salary: String(s.fte_monthly_salary),
      pensionOverride: s.pension_amount != null,
      pension_amount: s.pension_amount != null ? String(s.pension_amount) : "",
      utilization_pct: String(s.utilization_pct),
      hourly_rate: String(s.hourly_rate),
      payment_terms_id: s.payment_terms_id,
      vat_rate_id: s.vat_rate_id,
    });
    setPreview(null);
    setEstimate(null);
    setDialogOpen(true);
    api
      .get("/scenarios/pension-estimate", { params: { company_id: s.company_id } })
      .then((res) => setEstimate(res.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await api.post("/scenarios/preview", {
        company_id: companyId,
        employment_start: form.employment_start,
        assignment_start: form.assignment_start,
        fte_monthly_salary: Number(form.fte_monthly_salary),
        pension_amount: form.pensionOverride && form.pension_amount !== "" ? Number(form.pension_amount) : null,
        utilization_pct: Number(form.utilization_pct),
        hourly_rate: Number(form.hourly_rate),
        payment_terms_id: form.payment_terms_id,
        vat_rate_id: form.vat_rate_id,
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    const payload = {
      name: form.name,
      description: form.description || null,
      employment_start: form.employment_start,
      assignment_start: form.assignment_start,
      fte_monthly_salary: Number(form.fte_monthly_salary),
      pension_amount: form.pensionOverride && form.pension_amount !== "" ? Number(form.pension_amount) : null,
      utilization_pct: Number(form.utilization_pct),
      hourly_rate: Number(form.hourly_rate),
      payment_terms_id: form.payment_terms_id,
      vat_rate_id: form.vat_rate_id,
    };
    try {
      if (editing) {
        await api.patch(`/scenarios/${editing.id}`, payload);
        toast.success("Scenario updated");
      } else {
        await api.post("/scenarios", { ...payload, company_id: companyId, scenario_type: "HIRE_CONSULTANT" });
        toast.success("Scenario created — activate it to include it in Scenario Outcome");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/scenarios/${s.id}`, { active: !s.active });
      toast.success(s.active ? `Scenario "${s.name}" deactivated` : `Scenario "${s.name}" activated`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/scenarios/${deleteFor.id}`);
      toast.success(`Scenario "${deleteFor.name}" deleted — Official Forecast untouched`);
      setDeleteFor(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const activeCount = useMemo(() => (scenarios || []).filter((s) => s.active).length, [scenarios]);

  const formReady =
    form.name.trim() &&
    form.employment_start &&
    form.assignment_start &&
    Number(form.fte_monthly_salary) > 0 &&
    form.utilization_pct !== "" &&
    Number(form.utilization_pct) >= 0 &&
    Number(form.hourly_rate) > 0 &&
    form.payment_terms_id &&
    form.vat_rate_id &&
    (!form.pensionOverride || (form.pension_amount !== "" && Number(form.pension_amount) >= 0));

  return (
    <div data-testid="scenarios-page">
      <PageHeader
        title="Scenarios"
        subtitle="What-if planning — Official Forecast is never modified by scenarios"
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="sc-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="sc-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`sc-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canEdit && (
              <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="sc-new-button">
                <Plus className="h-4 w-4 mr-1.5" /> New scenario
              </Button>
            )}
          </div>
        }
      />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="sc-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before planning scenarios."
            testId="sc-no-years-state"
          />
        </div>
      )}

      {year && scenarios && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="sc-list">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/50 border-b border-slate-200">
                  {["Scenario", "Type", "Status", "Employment Start", "Assignment Start", `P&L Impact ${year}`, `Cash Impact ${year}`, ""].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`sc-row-${s.id}`}>
                    <td className="py-3 px-4">
                      <div className="text-sm font-semibold text-slate-800">{s.name}</div>
                      {s.description && <div className="text-xs text-slate-500">{s.description}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">Hire Consultant</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                          s.active ? "bg-[#ECFDF5] text-[#047857]" : "bg-slate-100 text-slate-500"
                        }`}
                        data-testid={`sc-status-${s.id}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${s.active ? "bg-[#047857]" : "bg-slate-400"}`} />
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{MONTH_LABEL(s.employment_start)}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{MONTH_LABEL(s.assignment_start)}</td>
                    <td className="py-3 px-4 text-sm font-semibold" data-testid={`sc-pnl-impact-${s.id}`}>
                      <span className={s.impact?.fy_pnl_impact < 0 ? "text-[#B91C1C]" : "text-[#047857]"}>
                        {s.impact ? `${fmtSigned(s.impact.fy_pnl_impact)} kr` : "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold" data-testid={`sc-cash-impact-${s.id}`}>
                      <span className={s.impact?.fy_cash_impact < 0 ? "text-[#B91C1C]" : "text-[#047857]"}>
                        {s.impact ? `${fmtSigned(s.impact.fy_cash_impact)} kr` : "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {canEdit && (
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`sc-edit-${s.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleActive(s)}
                            className={s.active ? "" : "text-[#047857] border-[#A7F3D0]"}
                            data-testid={`sc-toggle-${s.id}`}
                          >
                            <Power className="h-3.5 w-3.5 mr-1" /> {s.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteFor(s)} data-testid={`sc-delete-${s.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {scenarios.length === 0 && (
              <EmptyState
                title="No scenarios yet"
                description='Create your first scenario — e.g. "What happens if we hire another consultant?"'
                testId="sc-empty"
              />
            )}
          </div>

          {comparison && (
            <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="sc-comparison">
              <div className="px-4 py-3 border-b border-slate-200 bg-secondary/50 flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-[#C2410C]" />
                <span className="text-sm font-semibold text-slate-800">
                  Official Forecast vs Scenario Outcome — {year}
                </span>
                <span className="text-xs text-slate-500">
                  ({activeCount} active scenario{activeCount === 1 ? "" : "s"})
                </span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    {["Metric", "Official Forecast", "Scenario Outcome", "Difference"].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.metrics.map((m) => (
                    <tr key={m.key} className="border-b border-slate-100 last:border-0" data-testid={`sc-cmp-${m.key}`}>
                      <td className="py-2.5 px-4 text-sm font-medium text-slate-700">{m.label}</td>
                      <td className="py-2.5 px-4 text-sm text-slate-800">{m.kind === "percent" ? (m.official == null ? "N/A" : `${m.official}%`) : `${fmtSEK(m.official)} kr`}</td>
                      <td className="py-2.5 px-4 text-sm text-slate-800">{m.kind === "percent" ? (m.scenario == null ? "N/A" : `${m.scenario}%`) : `${fmtSEK(m.scenario)} kr`}</td>
                      <td className="py-2.5 px-4 text-sm font-semibold" data-testid={`sc-cmp-diff-${m.key}`}>
                        {m.diff == null ? "—" : m.kind === "percent" ? (
                          <span className={m.diff < 0 ? "text-[#B91C1C]" : m.diff > 0 ? "text-[#047857]" : "text-slate-500"}>
                            {m.diff > 0 ? "+" : ""}{m.diff} pp
                          </span>
                        ) : (
                          <span className={m.diff < 0 ? "text-[#B91C1C]" : m.diff > 0 ? "text-[#047857]" : "text-slate-500"}>
                            {fmtSigned(m.diff)} kr
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="sc-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit scenario — ${editing.name}` : "New scenario — Hire Consultant"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5 col-span-2">
              <Label>Scenario name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hire AWS Architect" data-testid="sc-form-name" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sc-form-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Employment start (month)</Label>
              <Input type="month" value={form.employment_start} onChange={(e) => setForm({ ...form, employment_start: e.target.value })} data-testid="sc-form-employment-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Expected assignment start (month)</Label>
              <Input type="month" value={form.assignment_start} onChange={(e) => setForm({ ...form, assignment_start: e.target.value })} data-testid="sc-form-assignment-start" />
            </div>
            <div className="space-y-1.5">
              <Label>FTE monthly salary (SEK)</Label>
              <Input type="number" value={form.fte_monthly_salary} onChange={(e) => setForm({ ...form, fte_monthly_salary: e.target.value })} placeholder="62000" data-testid="sc-form-salary" />
            </div>
            <div className="space-y-1.5">
              <Label>Employment</Label>
              <div className="h-9 flex items-center text-sm text-slate-500">100% (fixed for scenario hires)</div>
            </div>
            <div className="space-y-1.5 col-span-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pension-override"
                  checked={form.pensionOverride}
                  onChange={(e) => setForm({ ...form, pensionOverride: e.target.checked })}
                  data-testid="sc-form-pension-override"
                  className="h-4 w-4"
                />
                <Label htmlFor="pension-override" className="cursor-pointer">Override estimated pension</Label>
              </div>
              {form.pensionOverride ? (
                <Input type="number" value={form.pension_amount} onChange={(e) => setForm({ ...form, pension_amount: e.target.value })} placeholder="6000" data-testid="sc-form-pension" />
              ) : (
                <div className="text-xs text-slate-600 bg-secondary/50 border border-slate-200 rounded-md px-3 py-2" data-testid="sc-form-pension-estimate">
                  {estimate?.amount != null ? (
                    <>Estimated pension: <strong>{fmtSEK(estimate.amount)} SEK/month</strong> — Source: {estimate.label} ({estimate.sample_size} employee{estimate.sample_size === 1 ? "" : "s"})</>
                  ) : (
                    "No pension estimate available — enable override and enter a manual amount."
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Expected utilization (%)</Label>
              <Input type="number" value={form.utilization_pct} onChange={(e) => setForm({ ...form, utilization_pct: e.target.value })} data-testid="sc-form-utilization" />
            </div>
            <div className="space-y-1.5">
              <Label>Customer hourly rate (SEK/h)</Label>
              <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="1300" data-testid="sc-form-rate" />
            </div>
            <div className="space-y-1.5">
              <Label>Customer payment terms</Label>
              <Select value={form.payment_terms_id} onValueChange={(v) => setForm({ ...form, payment_terms_id: v })}>
                <SelectTrigger data-testid="sc-form-terms"><SelectValue placeholder="Select terms" /></SelectTrigger>
                <SelectContent>
                  {paymentTerms.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>VAT rate</Label>
              <Select value={form.vat_rate_id} onValueChange={(v) => setForm({ ...form, vat_rate_id: v })}>
                <SelectTrigger data-testid="sc-form-vat"><SelectValue placeholder="Select VAT" /></SelectTrigger>
                <SelectContent>
                  {vatRates.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name} ({v.rate}%)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading || !formReady} data-testid="sc-form-preview-button">
                {previewLoading ? "Calculating…" : "Preview impact"}
              </Button>
              {preview && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-testid="sc-form-preview">
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-slate-500">Est. monthly personnel cost</div>
                    <div className="font-semibold text-slate-900">{preview.monthly_personnel_cost == null ? "Incomplete (missing parameters)" : `${fmtSEK(preview.monthly_personnel_cost)} kr`}</div>
                  </div>
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-slate-500">Pension used</div>
                    <div className="font-semibold text-slate-900">{fmtSEK(preview.pension)} kr <span className="font-normal text-slate-500">({preview.pension_provenance?.source === "MANUAL" ? "Manual override" : preview.pension_provenance?.label})</span></div>
                  </div>
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-slate-500">Months employed before assignment</div>
                    <div className="font-semibold text-slate-900">{preview.months_employed_before_assignment}</div>
                  </div>
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-slate-500">First assignment month</div>
                    <div className="font-semibold text-slate-900">
                      {preview.first_assignment_month
                        ? `${MONTH_LABEL(`${preview.first_assignment_month.year}-${String(preview.first_assignment_month.month).padStart(2, "0")}`)} — ${preview.first_assignment_month.billable_hours}h → ${fmtSEK(preview.first_assignment_month.revenue)} kr`
                        : "—"}
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-md p-2.5 col-span-2">
                    <div className="text-slate-500">First cash receipt expected</div>
                    <div className="font-semibold text-slate-900">
                      {preview.first_cash_receipt_month
                        ? MONTH_LABEL(`${preview.first_cash_receipt_month[0]}-${String(preview.first_cash_receipt_month[1]).padStart(2, "0")}`)
                        : "—"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sc-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={!formReady} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="sc-form-save">
              {editing ? "Save changes" : "Create scenario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteFor} onOpenChange={(v) => !v && setDeleteFor(null)}>
        <AlertDialogContent data-testid="sc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete scenario "{deleteFor?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the scenario. No people, employment terms, assignments, actuals or Official Forecast data are affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sc-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="sc-delete-confirm">Delete scenario</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
